import config from '../config';
import Utils from '../Utils';
import ProxyService from '../Proxy';
import DialogsService from './xmpp/Dialogs';
import MessagesService from './xmpp/Messages';
import WebRTCSignalingProcessor from '../videocalling/WebRTCSignalingProcessor';
import type { Chat } from '../types';
import { ChatEvent, ProxyMethod } from '../types/enums';

export default abstract class AbstractChatService {
  public proxy: ProxyService;
  public dialog: DialogsService;
  public message: MessagesService;
  declare webrtcSignalingProcessor: WebRTCSignalingProcessor;

  public isConnected: boolean = false;
  protected isConnecting: boolean = false;
  protected isLogout: boolean = false;
  protected isReconnect: boolean = false;

  declare onChatStatusListener: Chat.OnChatStatusListener;
  declare onConnectionErrorListener: Chat.OnChatConnectionErrorListener;
  declare onDisconnectedListener: Chat.OnChatDisconnectedListener;
  declare onReconnectListener: Chat.OnChatReconnectedListener;
  declare onMessageListener: Chat.OnMessageListener;
  declare onSystemMessageListener: Chat.OnMessageSystemListener;
  declare onMessageTypingListener: Chat.OnMessageTypingListener;
  declare onMessageUpdateListener: Chat.OnMessageUpdateListener;
  declare onMessageDeleteListener: Chat.OnMessageDeleteListener;
  declare onMessageReactionsListener: Chat.OnMessageReactionsListener;
  declare onReadStatusListener: Chat.OnMessageReadListener;
  declare onLastUserActivityListener: Chat.OnLastUserActivityListener;
  declare onMessageErrorListener: Chat.OnMessageErrorListener;
  declare onDeliveredStatusListener: Chat.OnMessageDeliveredListener;
  declare onSentMessageCallback: Chat.OnMessageSentListener;
  declare onJoinOccupant: Chat.OnDialogJoinListener;
  declare onLeaveOccupant: Chat.OnDialogLeaveListener;
  declare onKickOccupant: Chat.OnDialogKickListener;

  constructor(proxy: ProxyService) {
    this.proxy = proxy;
    this.dialog = new DialogsService(proxy);
    this.message = new MessagesService(proxy);
  }

  public async search(params: Chat.SearchParams): Promise<Chat.SearchResult> {
    const query = Object.assign({}, params);

    if (query.start_date) {
      query.start_date = new Date(query.start_date).toISOString();
    }
    if (query.end_date) {
      query.end_date = new Date(query.end_date).toISOString();
    }
    if (Utils.isArray(query.chat_dialog_ids)) {
      query.chat_dialog_ids = (query.chat_dialog_ids as string[]).join(',');
    }

    const ajaxParams = {
      type: ProxyMethod.GET,
      url: Utils.getUrl(`${config.urls.chat}/search`),
      data: query,
    };

    return this.proxy.ajax(ajaxParams);
  }

  public abstract ping(): Promise<void>;

  public async pingWithTimeout(timeout: number = 5000): Promise<unknown> {
    return new Promise(async (resolve, reject) => {
      const error = `Failed to ping chat server ${config.chatProtocol.websocket}. Timed out after ${timeout}ms`;
      const timer = setTimeout(() => {
        Utils.safeCallbackCall(this.onConnectionErrorListener)(error);
        reject(error);
      }, timeout);

      try {
        const result = await this.ping();
        clearTimeout(timer);
        resolve(result);
      } catch {
        clearTimeout(timer);
        reject(error);
      }
    });
  }

  protected getListenerByName(name: ChatEvent): Chat.ListenerName | null {
    switch (name) {
      case ChatEvent.STATUS:
        return 'onChatStatusListener';
      case ChatEvent.ERROR:
        return 'onConnectionErrorListener';
      case ChatEvent.DISCONNECTED:
        return 'onDisconnectedListener';
      case ChatEvent.RECONNECTED:
        return 'onReconnectListener';
      case ChatEvent.MESSAGE:
        return 'onMessageListener';
      case ChatEvent.SYSTEM_MESSAGE:
        return 'onSystemMessageListener';
      case ChatEvent.ERROR_MESSAGE:
        return 'onMessageErrorListener';
      case ChatEvent.TYPING_MESSAGE:
        return 'onMessageTypingListener';
      case ChatEvent.UPDATE_MESSAGE:
        return 'onMessageUpdateListener';
      case ChatEvent.DELETE_MESSAGE:
        return 'onMessageDeleteListener';
      case ChatEvent.REACTIONS_MESSAGE:
        return 'onMessageReactionsListener';
      case ChatEvent.DELIVERED_MESSAGE:
        return 'onDeliveredStatusListener';
      case ChatEvent.READ_MESSAGE:
        return 'onReadStatusListener';
      case ChatEvent.SENT_MESSAGE:
        return 'onSentMessageCallback';
      case ChatEvent.USER_LAST_ACTIVITY:
        return 'onLastUserActivityListener';
      case ChatEvent.JOIN:
        return 'onJoinOccupant';
      case ChatEvent.LEAVE:
        return 'onLeaveOccupant';
      case ChatEvent.KICK:
        return 'onKickOccupant';
      default:
        return null;
    }
  }

  public addListener(name: ChatEvent, listener: Chat.Listeners): () => void {
    const listenerName = this.getListenerByName(name);
    if (listenerName) {
      (this[listenerName] as Chat.Listeners) = listener;
    }
    return this.removeListener.bind(this, name);
  }

  public removeListener(name: ChatEvent): void {
    const listenerName = this.getListenerByName(name);
    if (listenerName) {
      (this[listenerName] as Chat.Listeners | undefined) = undefined;
    }
  }

  public removeAllListeners(): void {
    Object.keys(this).forEach((key: string) => {
      if (key.startsWith('on') && key.endsWith('Listener') && typeof this[key as Chat.ListenerName] === 'function') {
        (this[key as Chat.ListenerName] as Chat.Listeners | undefined) = undefined;
      }
    });
  }
}
